import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { GitCompare, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { runAiJob } from '../../lib/aiJob';

const REL = { coincide: ['Coincide', 'border-emerald-500/40 text-emerald-300'], se_aparta: ['Se aparta', 'border-rose-500/40 text-rose-300'], matiza: ['Matiza', 'border-amber-500/40 text-amber-300'] };

const Result = ({ r }) => (
  <div data-testid={`doctrine-compare-result-${r.topic}`} className="mt-3 space-y-2 rounded-xl border border-slate-700 bg-slate-950/40 p-4 text-sm">
    {r.posicion_doctrina && <p className="text-slate-200"><b className="text-sky-300">Doctrina:</b> {r.posicion_doctrina}</p>}
    <p className="text-slate-300"><b className="text-amber-300">Síntesis:</b> {r.sintesis}</p>
    {(r.fallos || []).map((f) => (
      <div key={f.id} data-testid={`doctrine-compare-fallo-${f.id}`} className={`rounded-lg border p-2 text-xs ${REL[f.relacion]?.[1] || 'border-slate-700 text-slate-300'}`}>
        <b>{REL[f.relacion]?.[0] || f.relacion}</b> · {f.titulo} · {f.tribunal}{f.fecha ? ` · ${f.fecha}` : ''}{f.fuente === 'mis_causas' ? ' · tu causa' : ''}
        <p className="mt-0.5 text-slate-300">{f.explicacion}</p>
      </div>
    ))}
    {r.uso_estrategico && <p className="text-xs text-slate-400"><b>Cómo usarlo:</b> {r.uso_estrategico}</p>}
    {r.doctrine_refs?.length > 0 && <p className="text-[10px] text-slate-500">Extractos: {[...new Set(r.doctrine_refs.map((x) => `«${x.work}», p. ${x.page}`))].join(' · ')}</p>}
  </div>
);

// Doctrina vs fallos: per fixed topic, where case law (platform + your cases) agrees with or departs from the author
export const DoctrineVsRulings = ({ author }) => {
  const { api } = useAuth();
  const [topics, setTopics] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get('/doctrine/topics', { params: { author } }).then((r) => setTopics(r.data.topics)).catch(() => setTopics([])); setOpen(null); }, [author]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (t) => {
    setBusy(t.key); setOpen(t.key);
    try {
      const r = await runAiJob(api, '/doctrine/compare', { author, topic: t.key }, { timeout: 180000 });
      setTopics((ts) => ts.map((x) => (x.key === t.key ? { ...x, result: r } : x)));
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const cur = topics.find((t) => t.key === open);
  return (
    <div data-testid="doctrine-vs-rulings" className="mt-6 border-t border-slate-800 pt-5">
      <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-white"><GitCompare className="h-4 w-4 text-amber-400" />Doctrina vs fallos · {author}</h3>
      <p className="mb-3 text-xs text-slate-400">Elige un tema: Fidem compara la doctrina con los fallos de la plataforma y de tus causas y marca dónde coinciden, matizan o se apartan.</p>
      <div className="flex flex-wrap gap-1.5">
        {topics.map((t) => (
          <button key={t.key} data-testid={`doctrine-topic-${t.key}`} onClick={() => (t.result && open !== t.key ? setOpen(t.key) : run(t))} disabled={busy !== null}
            className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors disabled:opacity-60 ${open === t.key ? 'border-amber-500 bg-amber-500/15 text-amber-200' : 'border-slate-600 text-slate-300 hover:border-amber-500'}`}>
            {busy === t.key && <Loader2 className="h-3 w-3 animate-spin" />}{t.label}{t.result?.fallos?.length ? ` · ${t.result.fallos.length}` : ''}
          </button>
        ))}
      </div>
      {cur?.result && busy !== cur.key && (
        <>
          <Result r={cur.result} />
          <button data-testid="doctrine-compare-refresh" onClick={() => run(cur)} className="mt-2 text-[11px] text-amber-300 hover:underline">Volver a comparar</button>
        </>
      )}
    </div>
  );
};
